/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

/* Publish functions for the Adobe Client Data Layer (ACDL) */

import { ProductModel } from '../data/models';
import { transformProduct } from '../data/transforms/acdl-context';

/**
 * See: https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-sdk/src/contexts.ts
 */
export const contexts = {
  PRODUCT_CONTEXT: 'productContext',
  CHANNEL_CONTEXT: 'channelContext',
};

/**
 * See: https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-sdk/src/events.ts
 */
export const events = {
  PRODUCT_PAGE_VIEW: 'product-page-view',
};

export function getAdobeDataLayer() {
  // @ts-ignore
  window.adobeDataLayer = window.adobeDataLayer || [];
  // @ts-ignore
  return window.adobeDataLayer;
}

export function getChannelContext() {
  return {
    _id: 'https://ns.adobe.com/xdm/channels/web',
    _type: 'https://ns.adobe.com/xdm/channel-types/web',
  };
}

/**
 * Sets a context in the Adobe Client Data Layer (ACDL)
 * Logic based on: https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-sdk/src/Base.ts#L6
 */
function setContext(name: string, data: any) {
  const adobeDataLayer = getAdobeDataLayer();

  // Clear existing context
  adobeDataLayer.push({
    [name]: null,
  });

  // Set new context
  adobeDataLayer.push({
    [name]: data,
  });
}

export function setChannelContext() {
  if (typeof window === 'undefined') {
    return;
  }

  setContext(contexts.CHANNEL_CONTEXT, getChannelContext());
}

/**
 * Pushes an event to the Adobe Client Data Layer (ACDL)
 * Logic based on: https://github.com/adobe/commerce-events/blob/1973d0ce28471ef190fa06dad6359ffa0ab51db6/packages/storefront-events-sdk/src/Base.ts#L34
 */
function pushEvent(event: string, additionalContext?: any) {
  const adobeDataLayer = getAdobeDataLayer();

  adobeDataLayer.push((acdl: any) => {
    const state = acdl.getState ? acdl.getState() : {};

    acdl.push({
      event,
      eventInfo: {
        ...state,
        ...additionalContext,
      },
    });
  });
}

// Triggered when a product page is viewed
export function publishProductPageViewEvent(product: ProductModel) {
  const productItem = transformProduct(product);
  setContext(contexts.PRODUCT_CONTEXT, productItem);
  setChannelContext();
  pushEvent(events.PRODUCT_PAGE_VIEW);
}
